import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { Skeleton } from '../components/Skeleton';
import { TestIntro } from '../components/TestIntro';
import { paperTestSummary } from '../lib/paperTestPages';
import { useMartingale } from '../hooks/useMartingale';
import { ApiClientError, type MartingaleResponse } from '../lib/apiClient';
import {
  MARTINGALE_REPLAY,
  type MartingaleReplaySide,
} from '../lib/btc15MartingaleReplay';
import type { MartingaleSide } from '../lib/btc15Martingale';
import { formatSignedUsd } from '../lib/format';
import styles from './CrashBouncePage.module.css';

const usd = (x: number) => `$${x.toFixed(2)}`;
const cents = (x: number) => `${Math.round(x * 100)}¢`;
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const VERDICT_LABEL = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
} as const;

const SIDE_TITLE: Record<MartingaleSide, string> = {
  up: 'Always UP',
  down: 'Always DOWN',
};

export function MartingalePage() {
  const query = useMartingale();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Martingale data is temporarily unavailable.';
  return (
    <div className={styles.page}>
      <TestIntro
        heading={
          <div className={styles.heading}>
            <h1>Martingale</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/martingale')}
      >
        <p>
          Pick the same side of every BTC 15-minute round. After a loss, bet enough that a
          win recovers every loss so far plus $1. It wins small, often, until a long
          losing streak needs a stake the bankroll can&apos;t cover. This page counts
          every one of those busts.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — a demonstration, not a candidate."
        text="Paper only. Nothing on this page places an order. A martingale has no edge: it trades many small wins for rare large losses, and every safety rule on the real order path forbids it. A demonstration, not a candidate for real money."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading Martingale…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="14rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          {data.sides.map((side) => (
            <SideCard key={side.summary.side} side={side} data={data} />
          ))}
          <Replay />
          <p className={styles.fine}>
            Rules {data.rulesId}: a {usd(data.rules.bankroll)} paper bankroll per side,
            buying at the side&apos;s ask in the first archived quote of each round&apos;s
            first {Math.round(data.rules.entryWindowMs / 60_000)} minutes, sized so a win
            covers the chain&apos;s losses plus {usd(data.rules.targetProfit)} after
            Kalshi&apos;s taker fee, held to Kalshi&apos;s official result. When the
            bankroll can&apos;t cover the next stake, the run busts and is topped back up
            to {usd(data.rules.bankroll)}; P&amp;L counts every top-up. Rounds closing
            after {dayTime(Date.parse(data.registeredAt))} count.
            {(data.skippedNoQuote > 0 || data.skippedNoResult > 0) &&
              ` Skipped (no bet): ${data.skippedNoQuote} rounds with no opening quote, ${data.skippedNoResult} with no Kalshi result.`}{' '}
            {data.lastRunMs
              ? `Last run ${dayTime(data.lastRunMs)}.`
              : 'Waiting for the first run.'}
            {data.lastFailures.length > 0 &&
              ` Last run: ${data.lastFailures.join(', ')}; it retries in five minutes.`}
          </p>
        </>
      )}
    </div>
  );
}

function SideCard({
  side,
  data,
}: {
  side: MartingaleResponse['sides'][number];
  data: MartingaleResponse;
}) {
  const s = side.summary;
  const id = `martingale-${s.side}-heading`;
  return (
    <section className={styles.card} aria-labelledby={id}>
      <div className={styles.cardHead}>
        <h2 id={id}>{SIDE_TITLE[s.side]}</h2>
        <span className={`${styles.verdict} ${styles[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={styles.progress}>
        {s.days}/{data.passBar.minDays} days · {s.bets} bets · {s.cycles} chains won ·{' '}
        {s.busts} {s.busts === 1 ? 'bust' : 'busts'}
      </p>
      <dl className={styles.stats}>
        <div>
          <dt>P&amp;L after top-ups</dt>
          <dd className={`${tone(s.bets ? s.pnl : null)} ${styles.nowrap}`}>
            {s.bets ? formatSignedUsd(s.pnl) : '—'}
          </dd>
        </div>
        <div>
          <dt>Bankroll now</dt>
          <dd>{usd(s.bankroll)}</dd>
        </div>
        <div>
          <dt>Losing streak now</dt>
          <dd>{s.streak}</dd>
        </div>
      </dl>
      <p className={styles.small}>
        Paid in {usd(s.deposited)} ({usd(data.rules.bankroll)} plus every top-up). Longest
        losing streak {s.longestStreak}, biggest single stake {usd(s.maxStake)}, fees{' '}
        {usd(s.fees)}.
        {s.streak > 0 &&
          ` The current chain has lost ${usd(s.chainLoss)}; the next bet at 50¢ would cost ${usd(s.nextStakeAt50)}.`}
        {s.halves &&
          ` First half ${formatSignedUsd(s.halves.first)}, second half ${formatSignedUsd(s.halves.second)}.`}
      </p>
      {side.busts.length > 0 && (
        <>
          <h3 className={styles.small}>Busts</h3>
          <ul className={styles.list}>
            {side.busts.slice(0, 10).map((b) => (
              <li key={b.atMs} className={styles.row}>
                <div className={styles.rowHead}>
                  <strong>{dayTime(b.atMs)}</strong>
                  <span className={styles.down}>{formatSignedUsd(-b.lost)}</span>
                </div>
                <p className={styles.small}>After {b.streak} losses in a row</p>
              </li>
            ))}
          </ul>
        </>
      )}
      {side.recent.length > 0 && (
        <>
          <h3 className={styles.small}>Recent bets</h3>
          <ul className={styles.list}>
            {side.recent.slice(0, 12).map((r) => (
              <li key={r.closeMs} className={styles.row}>
                <div className={styles.rowHead}>
                  <strong>{dayTime(r.closeMs)}</strong>
                  <span className={r.won ? styles.up : styles.down}>
                    {r.won ? 'Won' : 'Lost'}
                  </span>
                </div>
                <p className={styles.small}>
                  {r.contracts} at {cents(r.ask)} for {usd(r.cost)} · bankroll{' '}
                  {usd(r.bankroll)}
                  {r.streak > 0 && ` · ${r.streak} losses in a row`}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
      <p className={styles.small}>
        Passes only after {data.passBar.minDays} days if P&amp;L, top-ups included, is
        above zero in both halves. Even then it stays paper: the real order path caps a
        bet at $2 and halts after 6 losses in a row.
      </p>
    </section>
  );
}

function ReplayStats({ label, side }: { label: string; side: MartingaleReplaySide }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={`${tone(side.pnl)} ${styles.nowrap}`}>
        {formatSignedUsd(side.pnl)}
      </dd>
      <p className={styles.small}>
        {side.busts} busts, {side.cycles} chains won, longest streak {side.longestStreak},
        biggest stake {usd(side.maxStake)}, fees {usd(side.fees)}
      </p>
    </div>
  );
}

function Replay() {
  const r = MARTINGALE_REPLAY;
  return (
    <section className={styles.card} aria-labelledby="martingale-replay-heading">
      <h2 id="martingale-replay-heading">Replay</h2>
      <p className={styles.small}>
        The same rules over {r.rounds.toLocaleString()} earlier rounds ({r.window}) at the
        site&apos;s archived opening asks and Kalshi&apos;s official results, before the
        test was registered. {r.note}
      </p>
      <dl className={styles.stats}>
        <ReplayStats label="Always UP" side={r.up} />
        <ReplayStats label="Always DOWN" side={r.down} />
      </dl>
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || Math.abs(x) < 0.005) return styles.flat;
  return x > 0 ? styles.up : styles.down;
}
